import React, { useMemo } from 'react'
import type { Chat, StateTransitionArgs } from '@ztechapps/common.contract'
import type { ChatStatusDetailType } from '@ztechapps/chat.common'
import { chatPermissions, chatStatusColor } from '@ztechapps/chat.common'
import { useError, useMutation, useRole } from '@ztechapps/common.hook'
import { StatusBreaddrumb } from '@ztechapps/webui.data-tools'
import { changeChatStatusMutation } from '../Inbox/graphql'
import { ChatStatus, ChatStatusBadge } from './ChatStatusBadge'

export type UpdateConversationStatusButtonProps = {
  // VL-8: the detail document the header itself already loaded (ConversationDetail.tsx's
  // `useDocument({ query: conversationDetailsQuery })`) — one source, so this control renders
  // the same status a deep-linked or off-page conversation shows in the header, instead of
  // going null against the inbox list page's own `chatList` (which never carries that page).
  chat?: Chat
}

export const UpdateConversationStatusButton = ({ chat }: UpdateConversationStatusButtonProps) => {
  const { hasRole: canEdit } = useRole({ roleName: chatPermissions.canEdit.value })

  const [changeStatus, , { error, loading }] = useMutation<{ changeChatStatus: Chat }, StateTransitionArgs<Chat>>({
    query: changeChatStatusMutation,
  })
  useError(error)

  // Every status stays offered whatever the conversation's current status is; the status-change
  // operation alone judges whether a move is legal.
  const statusMenu = useMemo<ChatStatusDetailType[]>(() => Object.values(chatStatusColor), [])

  // The detail document hasn't loaded yet (or the caller passed none) — no conversation to read
  // a status from or to fire the mutation against (mirrors MarkAsSpamButton's null-on-missing in
  // Inbox/ConversationDetail.tsx). Placed after every hook, same as that precedent.
  if (!chat?._id) return null

  const onUpdate = async (value: string) => {
    await changeStatus({ input: { filter: { _id: chat._id }, targetState: value } })
  }

  return (
    <StatusBreaddrumb
      status={chat.status ?? ''}
      loading={loading}
      component={{
        status: ChatStatus,
        statusBadge: ChatStatusBadge,
      }}
      statusMenu={statusMenu}
      onUpdate={onUpdate}
      disabled={!canEdit}
    />
  )
}
